import React, { useState } from 'react';
import { X, Loader2, CheckCircle } from 'lucide-react';

const ApplicationModal = ({ isOpen, onClose }) => {
  // 🔴 PASTE YOUR GOOGLE SCRIPT URL HERE
  const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxLTGa3EQYIL9ITKvkHEDeAfVB3Wjjkxw2B5ci6IzcX4w0A2kA87rtRfFrqUDPQO8m7/exec";

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    portfolio: '',
    role: 'Video Editor' // Default value
  });

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Send data to Google Sheets
      await fetch(SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify(formData),
        mode: "no-cors" // Important: bypasses CORS issues with Google Script
      });
      
      setSuccess(true);
      setLoading(false);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: '', email: '', phone: '', portfolio: '', role: 'Video Editor' });
        onClose();
      }, 3000);

    } catch (error) {
      console.error("Error!", error.message);
      setLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative bg-[#0B0B0B] border border-neutral-800 w-full max-w-lg p-8 rounded-lg shadow-2xl shadow-black">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        {!success ? (
          <>
            <h2 className="font-display text-3xl uppercase text-white mb-2">Request Access</h2>
            <p className="text-neutral-400 text-sm mb-8 font-light">
              Batch 01 is selective. Share your details and we will schedule a screening call.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Name Input */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#E50914] mb-2 font-mono">Full Name</label>
                <input 
                  type="text" 
                  name="name" 
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-neutral-900 border border-neutral-800 text-white p-4 focus:outline-none focus:border-[#E50914] transition-colors placeholder:text-neutral-700 font-light"
                  placeholder="e.g. Rahul Verma"
                />
              </div>

              {/* Email & Phone Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-mono">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-neutral-900 border border-neutral-800 text-white p-4 focus:outline-none focus:border-[#E50914] transition-colors placeholder:text-neutral-700 font-light"
                    placeholder="name@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-mono">Phone</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-neutral-900 border border-neutral-800 text-white p-4 focus:outline-none focus:border-[#E50914] transition-colors placeholder:text-neutral-700 font-light"
                    placeholder="+91 98765..."
                  />
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-mono">Current Role</label>
                <select 
                  name="role" 
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full bg-neutral-900 border border-neutral-800 text-white p-4 focus:outline-none focus:border-[#E50914] transition-colors font-light appearance-none"
                >
                  <option>Video Editor</option>
                  <option>Motion Designer</option>
                  <option>Filmmaker / DOP</option>
                  <option>Student</option>
                  <option>Other</option>
                </select>
              </div>

              {/* Portfolio Link */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-mono">Portfolio / Showreel Link</label>
                <input 
                  type="url" 
                  name="portfolio" 
                  required={false} // Make portfolio optional
                  value={formData.portfolio}
                  onChange={handleChange}
                  className="w-full bg-neutral-900 border border-neutral-800 text-white p-4 focus:outline-none focus:border-[#E50914] transition-colors placeholder:text-neutral-700 font-light"
                  placeholder="Drive, Vimeo, or Website URL"
                />
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#E50914] text-white font-bold uppercase tracking-widest py-4 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Submit Application"}
              </button>

            </form>
          </>
        ) : (
          /* Success State */
          <div className="text-center py-12">
            <CheckCircle className="mx-auto text-[#E50914] mb-6" size={64} />
            <h2 className="font-display text-3xl uppercase text-white mb-4">Application Received</h2>
            <p className="text-neutral-400 font-light">
              We have received your details.<br/>
              Our team will review your portfolio and contact you shortly.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default ApplicationModal;